
import React, { JSX, useState } from 'react'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'

const { VITE_CONTACT_API_URL: contactApiUrl } = import.meta.env

const contactApiBaseUrl = (): string | null => {
  if (typeof contactApiUrl !== 'string') return null
  const trimmed = contactApiUrl.trim()
  if (trimmed === '' || trimmed.includes('SET-ME')) return null
  return trimmed.replace(/\/+$/, '')
}

export const CONTACT_MESSAGE_MAX_LENGTH = 4000
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PRODUCT_TAG = 'flightdeck'

const validate = (email: string, message: string): string | null => {
  if (!EMAIL_SHAPE.test(email.trim())) {
    return 'Please enter a valid email address so we can reply to you.'
  }
  if (message.trim().length === 0) {
    return 'Please enter a message.'
  }
  if (message.length > CONTACT_MESSAGE_MAX_LENGTH) {
    return `Your message is too long, the limit is ${CONTACT_MESSAGE_MAX_LENGTH} characters.`
  }
  return null
}

const FIELD_CLASSES =
  'w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary placeholder:text-dm-text-accent focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60'

const SentConfirmation = (): JSX.Element => (
  <div
    data-testid='contact-form-sent'
    className='mt-8 flex items-start gap-2 rounded-md border border-emerald-500/20 bg-emerald-500/5 p-4'
  >
    <span className='mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400' aria-hidden='true' />
    <div>
      <h2 className='text-sm font-semibold text-emerald-300'>Message sent, thank you!</h2>
      <p className='mt-1 text-sm leading-relaxed text-dm-text-secondary'>
        The team reads every message that comes through this form and will
        reply to you by email.
      </p>
    </div>
  </div>
)

export const ContactForm = (): JSX.Element => {
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [website, setWebsite] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    if (busy) return

    const validationError = validate(email, message)
    if (validationError !== null) {
      setError(validationError)
      return
    }

    const base = contactApiBaseUrl()
    if (base === null) {
      setError(
        'The contact form is not connected in this environment yet. Please try again later.'
      )
      return
    }

    setBusy(true)
    setError(null)
    try {
      const res = await globalThis.fetch(`${base}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          message: message.trim(),
          product: PRODUCT_TAG,
          website
        })
      })
      if (!res.ok) throw new Error(`contact endpoint responded ${res.status}`)
      setSent(true)
    } catch {
      setError(
        'Something went wrong and your message was not sent. Please try again in a moment.'
      )
    } finally {
      setBusy(false)
    }
  }

  if (sent) return <SentConfirmation />

  return (
    <form
      onSubmit={(e) => { void handleSubmit(e) }}
      noValidate
      className='mt-8 space-y-4'
      data-testid='contact-form'
    >
      <div>
        <label
          htmlFor='contact-email'
          className='mb-1 block text-xs font-medium uppercase tracking-wider text-dm-text-accent'
        >
          Your email
        </label>
        <input
          id='contact-email'
          type='email'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder='you@example.com'
          autoComplete='email'
          disabled={busy}
          className={FIELD_CLASSES}
        />
      </div>

      <div>
        <label
          htmlFor='contact-message'
          className='mb-1 block text-xs font-medium uppercase tracking-wider text-dm-text-accent'
        >
          Message
        </label>
        <textarea
          id='contact-message'
          rows={6}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder='How can we help?'
          maxLength={CONTACT_MESSAGE_MAX_LENGTH}
          disabled={busy}
          className={FIELD_CLASSES}
        />
        <p className='mt-1 text-right text-[11px] text-dm-text-accent'>
          {message.length}/{CONTACT_MESSAGE_MAX_LENGTH}
        </p>
      </div>

      <div className='hidden' aria-hidden='true'>
        <label htmlFor='contact-website'>Website</label>
        <input
          id='contact-website'
          name='website'
          type='text'
          tabIndex={-1}
          autoComplete='off'
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      {error !== null && (
        <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>
      )}

      <button
        type='submit'
        disabled={busy}
        data-testid='contact-form-submit'
        className='inline-flex items-center justify-center gap-1.5 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
      >
        {busy && <LoadingSpinner />}
        {busy ? 'Sending…' : 'Send message'}
      </button>
    </form>
  )
}
